import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESEND_COOLDOWN = 60; // seconds

const SignInCode = () => {
  const { loginWithCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/progress';

  // Two-phase state: 'request' (email entry) → 'verify' (code entry)
  const [phase, setPhase] = useState('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Cooldown timer for "Resend code"
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Auto-focus first code input when phase flips to 'verify'
  useEffect(() => {
    if (phase === 'verify' && inputRefs.current[0]) inputRefs.current[0].focus();
  }, [phase]);

  const requestCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API}/auth/login-code/request`, { email: email.trim().toLowerCase() });
      setPhase('verify');
      setCooldown(RESEND_COOLDOWN);
      setCode(['', '', '', '', '', '']);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleCodeChange = (idx, val) => {
    // Only accept digits
    const digit = val.replace(/\D/g, '').slice(-1);
    const next = [...code];
    next[idx] = digit;
    setCode(next);
    setError('');
    // Auto-advance on entry
    if (digit && idx < 5 && inputRefs.current[idx + 1]) inputRefs.current[idx + 1].focus();
    // Auto-submit when all 6 digits filled
    if (next.every((d) => d !== '')) submitCode(next.join(''));
  };

  const handleCodeKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0 && inputRefs.current[idx - 1]) {
      inputRefs.current[idx - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
    setCode(next);
    if (pasted.length === 6) submitCode(pasted);
    else if (inputRefs.current[pasted.length]) inputRefs.current[pasted.length].focus();
  };

  const submitCode = async (full) => {
    setBusy(true);
    setError('');
    try {
      await loginWithCode(email.trim().toLowerCase(), full);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      // Clear code so user can retype cleanly
      setCode(['', '', '', '', '', '']);
      if (inputRefs.current[0]) inputRefs.current[0].focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16 flex items-center">
      <div className="max-w-md mx-auto w-full px-6">
        <div className="mb-6">
          <span className="enf-brand-pill inline-flex items-center">
            <img
              src="/brand/enferno-wordmark-nav@2x.png"
              srcSet="/brand/enferno-wordmark-nav.png 1x, /brand/enferno-wordmark-nav@2x.png 2x"
              alt="Enferno"
              className="h-12 w-auto"
              loading="eager"
            />
          </span>
        </div>

        {phase === 'request' ? (
          <>
            <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2">Sign in by code</div>
            <h1 className="font-display text-5xl tracking-wide leading-none mb-3">No password.</h1>
            <p className="text-enf-text-secondary mb-8 text-sm">Drop your email and we&apos;ll send a 6-digit code.</p>

            <form onSubmit={requestCode} className="space-y-4" data-testid="signin-code-request-form">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Email</span>
                <input
                  type="email" required autoComplete="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                  data-testid="signin-code-email"
                  autoFocus
                />
              </label>

              {error && (
                <div className="px-3 py-2 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded-md text-sm text-enf-role-danger" data-testid="signin-code-error">{error}</div>
              )}

              <button
                type="submit" disabled={busy}
                className="w-full py-3 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                data-testid="signin-code-send"
              >
                {busy ? 'Sending...' : <>Send sign-in code <ArrowRight className="w-4 h-4" /></>}
              </button>

              <div className="text-center text-sm text-enf-text-muted pt-2">
                Prefer a password?{' '}
                <Link to="/login" className="text-enf-role-info hover:text-enf-text-primary font-semibold">Sign in normally</Link>
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> Check your inbox
            </div>
            <h1 className="font-display text-5xl tracking-wide leading-none mb-3">6 digits.</h1>
            <p className="text-enf-text-secondary mb-2 text-sm">
              We sent a code to <span className="text-enf-text-primary">{email}</span>. Type it below.
            </p>
            <p className="text-xs text-enf-text-muted mb-6 leading-relaxed">
              Don&apos;t see it? First-time emails from us sometimes land in spam or promotions — check those folders before requesting another.
            </p>

            <div className="flex justify-between gap-2 mb-5" onPaste={handlePaste} data-testid="signin-code-input-row">
              {code.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => { inputRefs.current[idx] = el; }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(idx, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                  className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg focus:ring-1 focus:ring-enf-accent/40"
                  data-testid={`signin-code-digit-${idx}`}
                  aria-label={`Digit ${idx + 1}`}
                />
              ))}
            </div>

            {error && (
              <div className="px-3 py-2 mb-4 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded-md text-sm text-enf-role-danger" data-testid="signin-code-verify-error">{error}</div>
            )}

            {busy && (
              <div className="text-center text-sm text-enf-text-muted mb-4">Verifying...</div>
            )}

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => { setPhase('request'); setError(''); }}
                className="flex items-center gap-1.5 text-enf-text-muted hover:text-enf-text-primary transition-colors"
                data-testid="signin-code-back"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Different email
              </button>
              <button
                type="button"
                onClick={requestCode}
                disabled={cooldown > 0 || busy}
                className="flex items-center gap-1.5 text-enf-role-info hover:text-enf-text-primary disabled:text-enf-text-muted disabled:cursor-not-allowed transition-colors"
                data-testid="signin-code-resend"
              >
                <Mail className="w-3.5 h-3.5" />
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>

            <div className="mt-8 text-center text-xs text-enf-text-muted leading-relaxed">
              Code expires in 10 minutes. Didn&apos;t get one? Check spam, or{' '}
              <Link to="/login" className="text-enf-role-info hover:text-enf-text-primary">sign in with password</Link>.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SignInCode;
